import { nigeriaStatesAndLGAs } from "./nigeriaStatesAndLGAs";
import { usStateOptions } from "./usStates";
import {
  beninDepartmentOptions,
  ghanaRegionOptions,
  kenyaCountyOptions,
  australiaStateOptions,
  ukCountyOptions,
  canadaProvinceOptions,
} from "./internationalStates";

export const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
export const BACKEND_ADDRESS =
  process.env.REACT_APP_BACKEND_ADDRESS || "http://localhost:8080/";

// Mobile app custom URL scheme — must match whichever native build this
// environment should deep-link into (prod "ngwaahia" vs dev-client
// "ngwaahia-dev", see ngwaahia-mobile/app.config.js). Set per Railway
// environment via REACT_APP_APP_SCHEME so dev.ngwaahia.com opens the dev
// client and ngwaahia.com opens the prod TestFlight/Play Store app.
export const APP_SCHEME = process.env.REACT_APP_APP_SCHEME || "ngwaahia";

export const IOS_STORE_URL =
  process.env.REACT_APP_IOS_STORE_URL ||
  "https://apps.apple.com/app/id6788688031";
export const ANDROID_STORE_URL =
  process.env.REACT_APP_ANDROID_STORE_URL ||
  "https://play.google.com/store/apps/details?id=com.prostec.ngwaahia";

// The Roll Credits soundtrack, hosted in the media bucket under static/. Empty
// means the page plays no music, which is the safe default for an environment
// whose bucket has no copy of the file yet.
export const CREDITS_SOUNDTRACK_URL =
  process.env.REACT_APP_CREDITS_SOUNDTRACK_URL || "";

export const resolveImageUri = (image) => {
  if (!image?.path) return "";
  if (image.path.startsWith("http")) return image.path;
  return `${BACKEND_ADDRESS}${image.path}`;
};

// Mirrors ngwaahia-mobile/utils/constants.js — keep in sync when categories change
export const shopCategories = [
  {
    label: "Agricultural Products",
    value: "AGRICULTURAL_PRODUCTS",
    icon: "🌾",
  },
  { label: "Arts & Crafts", value: "ARTS_AND_CRAFTS", icon: "🎨" },
  { label: "Baby & Kids", value: "BABY_AND_KIDS", icon: "🍼" },
  {
    label: "Beauty & Personal Care",
    value: "BEAUTY_AND_PERSONAL_CARE",
    icon: "💄",
  },
  { label: "Books & Stationery", value: "BOOKS_AND_STATIONERY", icon: "📚" },
  { label: "Cars & Automotive", value: "CARS_AND_AUTOMOTIVE", icon: "🚗" },
  {
    label: "Electronics & Gadgets",
    value: "ELECTRONICS_AND_GADGETS",
    icon: "📱",
  },
  { label: "Fabrics & Textiles", value: "FABRICS_AND_TEXTILES", icon: "🧵" },
  { label: "Fashion & Clothing", value: "FASHION_AND_CLOTHING", icon: "👗" },
  {
    label: "Food, Drinks & Groceries",
    value: "FOOD_DRINKS_AND_GROCERIES",
    icon: "🥣",
  },
  {
    label: "Furniture & Home Appliances",
    value: "FURNITURE_AND_HOME_APPLIANCES",
    icon: "🛋️",
  },
  { label: "Gifts & Souvenirs", value: "GIFTS_AND_SOUVENIRS", icon: "🎁" },
  { label: "Health & Wellness", value: "HEALTH_AND_WELLNESS", icon: "💊" },
  { label: "Home Decor", value: "HOME_DECOR", icon: "🏡" },
  {
    label: "Jewelry, Watches & Accessories",
    value: "JEWELRY_WATCHES_AND_ACCESSORIES",
    icon: "💍",
  },
  { label: "Kitchen & Household", value: "KITCHEN_AND_HOUSEHOLD", icon: "🍳" },
  {
    label: "Music & Entertainment",
    value: "MUSIC_AND_ENTERTAINMENT",
    icon: "🎵",
  },
  { label: "Office Supplies", value: "OFFICE_SUPPLIES", icon: "🖊️" },
  { label: "Outdoor & Adventure", value: "OUTDOOR_AND_ADVENTURE", icon: "🏕️" },
  { label: "Pet Supplies", value: "PET_SUPPLIES", icon: "🐾" },
  {
    label: "Phone & Computer Accessories",
    value: "PHONE_AND_COMPUTER_ACCESSORIES",
    icon: "🔌",
  },
  {
    label: "Religious & Cultural Items",
    value: "RELIGIOUS_AND_CULTURAL_ITEMS",
    icon: "🙏",
  },
  { label: "Services", value: "SERVICES", icon: "🛠️" },
  { label: "Sports & Fitness", value: "SPORTS_AND_FITNESS", icon: "⚽" },
  { label: "Toys & Games", value: "TOYS_AND_GAMES", icon: "🧸" },
  { label: "Travel & Luggage", value: "TRAVEL_AND_LUGGAGE", icon: "🧳" },
  { label: "Other", value: "OTHER", icon: "📦" },
];

export const deliveryMethodOptions = [
  { label: "Pickup", value: "PICKUP" },
  { label: "Delivery", value: "DELIVERY" },
  { label: "Service", value: "SERVICE" },
];

// Mirrors DELIVERY_CARRIERS in the backend's utility/deliveryTracking.js.
export const DELIVERY_CARRIER_LABELS = {
  OWN_RIDER: "Own rider or driver",
  DISPATCH_RIDER: "Dispatch rider",
  GIG_LOGISTICS: "GIG Logistics",
  DHL: "DHL",
  FEDEX: "FedEx",
  UPS: "UPS",
  USPS: "USPS",
  ROYAL_MAIL: "Royal Mail",
  AUSTRALIA_POST: "Australia Post",
  CANADA_POST: "Canada Post",
  OTHER: "Other",
};

export const NAME_PRONUNCIATION = {
  respelling: "ngwah-HYAH",
  ipa: "[ŋɡʷaːɦja]",
};

export const paymentMethodOptions = [
  { label: "Cash on Delivery", value: "CASH_ON_DELIVERY" },
  { label: "Bank Transfer", value: "BANK_TRANSFER" },
  { label: "Mobile Money", value: "MOBILE_MONEY" },
  { label: "Venmo", value: "VENMO" },
  { label: "Zelle", value: "ZELLE" },
];

// Short labels for filter chips (mirrors ngwaahia-mobile's paymentTimingFilterOptions).
export const paymentTimingFilterOptions = [
  { label: "Pay upfront", value: "UPFRONT" },
  { label: "Pay at pickup", value: "ON_PICKUP" },
  { label: "Pay on delivery", value: "ON_DELIVERY" },
];

export const productSortOptions = [
  { label: "Top rated", value: "top_rated" },
  { label: "Most sold", value: "most_sold" },
  { label: "Price ↑", value: "price_asc" },
  { label: "Price ↓", value: "price_desc" },
  { label: "Newest", value: "newest" },
  { label: "Oldest", value: "oldest" },
];

export const shopSortOptions = [
  { label: "Top rated", value: "top_rated" },
  { label: "Newest", value: "newest" },
  { label: "Oldest", value: "oldest" },
];

export const countryOptions = [
  { label: "Nigeria", value: "NG" },
  { label: "Ghana", value: "GH" },
  { label: "Kenya", value: "KE" },
  { label: "Benin Republic", value: "BJ" },
  { label: "United States", value: "US" },
  { label: "United Kingdom", value: "GB" },
  { label: "Australia", value: "AU" },
  { label: "Canada", value: "CA" },
];

// Single source of truth — derived so adding a country to countryOptions
// propagates everywhere (phone country pickers, account settings labels, etc.)
// instead of each consumer keeping its own hardcoded list that can drift.
export const SUPPORTED_COUNTRY_CODES = countryOptions.map((c) => c.value);
export const COUNTRY_LABELS_BY_CODE = Object.fromEntries(
  countryOptions.map((c) => [c.value, c.label]),
);

const nigeriaStateOptions = [...nigeriaStatesAndLGAs]
  .map((s) => s.state)
  .sort()
  .map((state) => ({ label: state, value: state }));

export const getStateOptionsForCountry = (country) => {
  switch (country) {
    case "NG":
      return nigeriaStateOptions;
    case "US":
      return usStateOptions;
    case "GH":
      return ghanaRegionOptions;
    case "KE":
      return kenyaCountyOptions;
    case "BJ":
      return beninDepartmentOptions;
    case "AU":
      return australiaStateOptions;
    case "GB":
      return ukCountyOptions;
    case "CA":
      return canadaProvinceOptions;
    default:
      return null;
  }
};
